<script setup>
import { computed, ref } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  setAppearance,
  temaGuardado,
} from 'dashboard/composables/commands/useAppearanceHotKeys';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

defineProps({
  isCollapsed: { type: Boolean, default: false },
  inverted: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      // Sem teclado no celular: o item some lá (`.perfil-so-teclado`).
      class: 'perfil-so-teclado',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-scroll-text',
      link: 'https://www.chatwoot.com/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

// O TEMA NO PRÓPRIO MENU (08/10/2026). "Alterar tema" abria a paleta antiga
// (outra cara, rodapé em inglês) só para escolher entre três opções. Agora é
// o segmentado do produto, aqui, com a escolha atual marcada.
const tema = ref(temaGuardado());
const opcoesDeTema = computed(() => [
  {
    chave: 'light',
    rotulo: t('COMMAND_BAR.COMMANDS.LIGHT_MODE'),
    icone: 'i-lucide-sun',
  },
  {
    chave: 'dark',
    rotulo: t('COMMAND_BAR.COMMANDS.DARK_MODE'),
    icone: 'i-lucide-moon',
  },
  {
    chave: 'auto',
    rotulo: t('COMMAND_BAR.COMMANDS.SYSTEM_MODE'),
    icone: 'i-lucide-monitor',
  },
]);
const escolherTema = chave => {
  tema.value = chave;
  setAppearance(chave);
};

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center p-1 text-left rounded-lg cursor-pointer"
        :class="[
          inverted
            ? ['hover:bg-white/10', isOpen && 'bg-white/10']
            : ['hover:bg-n-alpha-1', isOpen && 'bg-n-alpha-1'],
          isCollapsed ? 'justify-center' : 'w-full',
        ]"
        :title="isCollapsed ? currentUser.available_name : undefined"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div v-if="!isCollapsed" class="min-w-0">
          <div
            class="text-sm font-medium leading-4 truncate"
            :class="inverted ? 'text-white' : 'text-n-slate-12'"
          >
            {{ currentUser.available_name }}
          </div>
          <div
            class="text-xs truncate"
            :class="inverted ? 'text-white/70' : 'text-n-slate-11'"
          >
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <!-- Mesmo caso do painel do sino: este menu tambem e filho do header com
         `.sidebar-dark` e herdava as cores da barra escura. -->
    <DropdownBody
      class="advup-tema-pagina z-50 w-80"
      :class="
        inverted
          ? 'top-full mt-2 ltr:right-0 rtl:left-0'
          : 'bottom-12 mb-2 ltr:left-0 rtl:right-0'
      "
    >
      <!-- No celular o botão é só o rosto: o menu diz quem está entrando. -->
      <div class="perfil-quem">
        <Avatar
          :size="36"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          rounded-full
        />
        <div class="min-w-0">
          <p class="perfil-quem__nome">{{ currentUser.available_name }}</p>
          <p class="perfil-quem__email">{{ currentUser.email }}</p>
        </div>
      </div>
      <SidebarProfileMenuStatus />
      <div class="perfil-tema">
        <span class="perfil-tema__rot">{{ t('SIDEBAR_ITEMS.TEMA') }}</span>
        <div class="advup-seg perfil-tema__seg" role="radiogroup">
          <button
            v-for="opcao in opcoesDeTema"
            :key="opcao.chave"
            type="button"
            role="radio"
            class="advup-seg__btn perfil-tema__btn"
            :class="{ 'advup-seg__btn--on': tema === opcao.chave }"
            :aria-checked="tema === opcao.chave"
            @click="escolherTema(opcao.chave)"
          >
            <i :class="opcao.icone" aria-hidden="true" />
            <span>{{ opcao.rotulo }}</span>
          </button>
        </div>
      </div>
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>

<style scoped>
.perfil-quem {
  display: none;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid rgb(var(--slate-4));
}

.perfil-quem__nome {
  margin: 0;
  overflow: hidden;
  color: rgb(var(--slate-12));
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.perfil-quem__email {
  margin: 0;
  overflow: hidden;
  color: rgb(var(--slate-11));
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.perfil-tema {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
}

.perfil-tema__rot {
  color: rgb(var(--slate-12));
  font-size: 14px;
}

.perfil-tema__btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding-inline: 8px;
}

.perfil-tema__btn > i {
  width: 14px;
  height: 14px;
}

@media (max-width: 639px) {
  .perfil-quem {
    display: flex;
  }

  :deep(.perfil-so-teclado) {
    display: none;
  }
}
</style>
